/**
 * Hooks for SOP Health tab (Phase 23.3.3 — Living SOPs)
 *
 * Powers the Health tab on the SOP detail page: a per-section heatmap of
 * active signals, a list of pending proposals + active suppressions, a
 * full proposal history table, and a trainer-gated "Generate proposal"
 * manual trigger.
 *
 * Polling: `useSopHealth` refetches every 60s so a trainer leaving the
 * tab open sees newly drafted proposals land without a manual reload.
 * This mirrors the 60s cadence on the proposals inbox query.
 *
 * Cache invalidation: successful manual triggers invalidate both the
 * health query (the new pending proposal will show up under
 * activeSignals) and the proposals inbox list so the drafter's output
 * surfaces in /sops/proposals within one refetch cycle.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';
import { sopProposalKeys } from './useSopProposals';

// ── Response shapes ─────────────────────────────────────────────────────────
// Must stay in sync with `GET /api/sops/:sopId/health` in apps/api/src/routes/sops.ts.

export type SopHealthStatus = 'healthy' | 'attention' | 'critical';

export interface SopHealthSectionSummary {
  sectionId: string;
  sectionTitle: string;
  sectionNumber: number;
  activeSignalCount: number;
  healthStatus: SopHealthStatus;
  pendingProposals: number;
  activeSuppressions: number;
}

export type SopHealthSignalType = 'pending_proposal' | 'suppressed';

export interface SopHealthActiveSignal {
  sectionId: string | null;
  sectionTitle: string | null;
  signalType: SopHealthSignalType;
  source: string | null;
  createdAt: string;
  // Only for pending_proposal
  proposalId?: string;
  rationale?: string;
  impactScore?: number;
  confidence?: number;
  // Only for suppressed
  suppressionReason?: string;
  suppressedUntil?: string | null;
}

export interface SopHealthProposalHistoryItem {
  proposalId: string;
  source: string;
  state: string;
  rationale: string;
  impactScore: number;
  confidence: number;
  reviewerUserName?: string;
  reviewedAt?: string;
  createdAt: string;
}

export interface SopHealthResponse {
  sopId: string;
  sopTitle: string;
  sectionHealth: SopHealthSectionSummary[];
  activeSignals: SopHealthActiveSignal[];
  proposalHistory: SopHealthProposalHistoryItem[];
}

// ── Query key factory ───────────────────────────────────────────────────────

export const sopHealthKeys = {
  all: ['sopHealth'] as const,
  detail: (sopId: string) => [...sopHealthKeys.all, sopId] as const,
};

// ── Hooks ───────────────────────────────────────────────────────────────────

/**
 * Fetch the SOP Health payload (heatmap + signals + history).
 *
 * Gated on `sopId` presence — rendering the Health tab before the SOP
 * detail has resolved would fire with `undefined`, so we disable the
 * query until a real UUID lands.
 */
export function useSopHealth(sopId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: sopHealthKeys.detail(sopId ?? ''),
    queryFn: () =>
      api.get<SopHealthResponse>(`/api/sops/${sopId}/health`),
    enabled: !!sopId,
    refetchInterval: 60_000, // blueprint §Chunk 23.3.3
    staleTime: 30_000,
  });
}

// ── Manual trigger mutation ─────────────────────────────────────────────────

export interface TriggerSopProposalInput {
  sopId: string;
  sectionId: string;
  stepId?: string;
  sourceDescription?: string;
}

export interface TriggerSopProposalResult {
  jobId: string | null;
  status: 'queued';
}

/**
 * Mutation: enqueue a manual AI-drafter trigger for a (sopId, sectionId)
 * pair. Trainer-gated server-side (requireRole('trainer')).
 *
 * On success we invalidate:
 *   - The health query for this SOP — the soon-to-land proposal shows
 *     up under `activeSignals` after its next tick.
 *   - The proposals inbox list — the new proposal surfaces in the
 *     pending_review view the trainer normally reviews from.
 *
 * Errors surface to the caller; the UI renders the message in a toast.
 */
export function useTriggerSopProposal() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<TriggerSopProposalResult, Error, TriggerSopProposalInput>({
    mutationFn: ({ sopId, sectionId, stepId, sourceDescription }) =>
      api.post<TriggerSopProposalResult>(
        `/api/sops/${sopId}/sections/${sectionId}/trigger-proposal`,
        { stepId, sourceDescription }
      ),
    onSuccess: (_result, { sopId }) => {
      queryClient.invalidateQueries({
        queryKey: sopHealthKeys.detail(sopId),
      });
      queryClient.invalidateQueries({ queryKey: sopProposalKeys.all });
    },
  });
}
